import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Table, Button } from "react-bootstrap";
import { ArrowLeft, Eye, Download, Bookmark, Search, ChevronLeft, ChevronRight } from "lucide-react";

const API_BASE_URL = `${process.env.REACT_APP_BASE_API_URL}`;

const getStatusDisplay = (status) => {
  if (!status) return 'Pending';
  const lower = status.toLowerCase().trim();
  if (lower === 'opened') return 'Opened';
  if (lower === 'qualified') return 'Qualified';
  if (lower === 'disqualified') return 'Disqualified';
  if (lower === 'pending') return 'Pending';
  if (lower === 'approved') return 'Approved';
  if (lower === 'passed') return 'Passed';
  if (lower === 'failed') return 'Failed';
  if (lower === 'rejected') return 'Rejected';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const getStatusColor = (status) => {
  if (!status) return '#6b7280';
  const lower = status.toLowerCase().trim();
  if (lower === 'opened' || lower === 'qualified' || lower === 'approved' || lower === 'passed') return '#10b981';
  if (lower === 'disqualified' || lower === 'failed' || lower === 'rejected') return '#ef4444';
  if (lower === 'pending' || lower === 'under review') return '#f59e0b';
  return '#6b7280';
};

const getBidderStatus = (bidder, statusType) => {
  let rawStatus = '';
  if (statusType === 'technical') {
    rawStatus = bidder.technical_status || bidder.technical_status_raw || '';
  } else {
    rawStatus = bidder.financial_status || bidder.financial_status_raw || '';
  }
  return getStatusDisplay(rawStatus);
};

export default function TendersAwarded2() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenderData, setTenderData] = useState(null);
  const [views, setViews] = useState(0);
  const [bookmarks, setBookmarks] = useState(0);
  const [applies, setApplies] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  useEffect(() => {
    if (tenderId) {
      fetchTenderData();
      fetchViews(tenderId);
      fetchApplies(tenderId);
      fetchFavourites(tenderId);
    }
  }, [tenderId]);

  const fetchTenderData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/tender-award/${tenderId}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError('Tender not found');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        setLoading(false);
        return;
      }
      const data = await response.json();
      if (data.success) {
        setTenderData(data);
      } else {
        setError(data.error || 'Failed to fetch tender data');
      }
    } catch (err) {
      console.error('Error fetching tender:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchViews = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenders/${id}/views`);
      const data = await response.json();
      if (data.success) setViews(data.views || 0);
    } catch (err) {
      console.error('Error fetching views:', err);
    }
  };

  const fetchApplies = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenders/${id}/applies`);
      const data = await response.json();
      if (data.success) setApplies(data.applies || 0);
    } catch (err) {
      console.error('Error fetching applies:', err);
    }
  };

  const fetchFavourites = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenders/${id}/favourites-count`);
      const data = await response.json();
      if (data.success) setBookmarks(data.count || 0);
    } catch (err) {
      console.error('Error fetching favourites:', err);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === '0') return 'N/A';
    const num = parseFloat(amount);
    if (isNaN(num) || num === 0) return 'N/A';
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const getTimelineStatus = (stepDate) => {
    if (!stepDate || stepDate === 'N/A' || stepDate === 'Pending') return 'inactive';
    try {
      const date = new Date(stepDate);
      if (isNaN(date.getTime())) return 'inactive';
      const now = new Date();
      return date <= now ? 'passed' : 'active';
    } catch {
      return 'inactive';
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading tender details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
        <div style={{ color: 'red', marginBottom: '16px' }}>{error}</div>
        <button onClick={() => navigate(-1)} style={{ padding: '8px 16px', cursor: 'pointer' }}>
          Go Back
        </button>
      </div>
    );
  }

  if (!tenderData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>No tender found</div>
      </div>
    );
  }

  const { tender, evaluation } = tenderData;
  const safeTender = tender || {};
  const safeEvaluation = evaluation || [];
  const l1Winner = safeEvaluation.find(e => e.is_winner) || safeEvaluation[0] || null;
  const displayTitle = safeTender.project_name || safeTender.work_name || tenderData.tender_number || 'Tender Details';
  const displayDepartment = safeTender.department_name || safeTender.organization_name || 'N/A';
  const displayMinistry = safeTender.ministry_name || 'N/A';
  const displayLocation = safeTender.location || 'N/A';
  const displayValue = safeTender.estimated_value || null;
  const isAwarded = l1Winner !== null;

  const filteredBidders = safeEvaluation.filter(b => 
    b.seller_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.rank?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.offered_item?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBidders = filteredBidders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBidders.length / itemsPerPage);

  const timelineSteps = [
    { label: "Published", date: formatDate(safeTender.published_date || safeTender.created_at), imgSrc: "/icons/published.png" },
    { label: "Pre-bid Meeting", date: formatDate(safeTender.pre_bid_meeting_date) || 'N/A', imgSrc: "/icons/prebid.png" },
    { label: "Submission Deadline", date: formatDate(safeTender.submission_deadline), imgSrc: "/icons/submission.png" },
    { label: "Technical Opening", date: formatDate(safeTender.bid_open_date) || 'N/A', imgSrc: "/icons/technical.png" },
    { label: "Award Date", date: l1Winner ? formatDate(new Date()) : 'Pending', imgSrc: "/icons/award.png" },
  ];

  return (
    <div style={{ backgroundColor: "#ffffff", minHeight: "100vh", paddingBottom: "60px", fontFamily: "'Inter', sans-serif" }}>
      <Container fluid style={{ maxWidth: "1400px", padding: "24px 40px" }}>
       
        <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "24px", display: "flex", gap: "8px" }}>
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>Home</span>
          <span>&gt;</span>
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/results')}>Tender Results</span>
          <span>&gt;</span>
          <span style={{ color: "#111827", fontWeight: "500" }}>{displayTitle.substring(0, 40)}...</span>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <ArrowLeft size={24} color="#374151" style={{ cursor: "pointer" }} onClick={() => navigate(-1)} />
        </div>

        <div style={{ backgroundColor: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "24px 20px", marginBottom: "24px" }}>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div className="d-flex align-items-center gap-4 flex-wrap">
              <h2 style={{ fontSize: "26px", fontWeight: "700", color: "#111827", margin: 0 }}>{displayTitle}</h2>
              {isAwarded ? (
                <div style={{ 
                  backgroundColor: "#d1fae5",
                  color: "#065f46",
                  borderRadius: "50px", 
                  padding: "6px 16px", 
                  fontSize: "13px", 
                  fontWeight: "600", 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "8px" 
                }}>
                  <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#10b981" }} />
                  Awarded
                </div>
              ) : (
                <div style={{ 
                  backgroundColor: "#fef3c7",
                  color: "#92400e",
                  borderRadius: "50px", 
                  padding: "6px 16px", 
                  fontSize: "13px", 
                  fontWeight: "600", 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "8px" 
                }}>
                  <div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#f59e0b" }} />
                  Pending
                </div>
              )}
            </div>

            <div style={{ 
              position: "relative", 
              width: "500px", 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "flex-start",
              paddingTop: "5px"
            }}>
              <div style={{ 
                position: "absolute", 
                top: "15px", 
                left: "20px", 
                right: "20px", 
                height: "2px", 
                backgroundColor: "#e5e7eb", 
                zIndex: 0 
              }} />
              
              {timelineSteps.map((step, idx) => {
                const status = getTimelineStatus(step.date);
                const isPassed = status === 'passed';
                const isActive = status === 'active';
                const bgColor = isPassed ? '#2F7A87' : (isActive ? '#2F7A87' : '#e5e7eb');
                const textColor = isPassed ? '#2F7A87' : (isActive ? '#2F7A87' : '#6b7280');
                const isLast = idx === timelineSteps.length - 1;
                
                return (
                  <div key={idx} style={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    alignItems: "center", 
                    zIndex: 1, 
                    backgroundColor: "#ffffff", 
                    padding: "0 2px",
                    position: "relative",
                    flex: 1
                  }}>
                    {!isLast && (
                      <div style={{
                        position: "absolute",
                        top: "15px",
                        left: "calc(50% + 15px)",
                        right: "calc(-50% + 15px)",
                        height: "2px",
                        backgroundColor: isPassed ? '#2F7A87' : '#e5e7eb',
                        zIndex: 0
                      }} />
                    )}
                    
                    <div style={{ 
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      backgroundColor: bgColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1,
                      position: "relative",
                      flexShrink: 0,
                      marginBottom: "6px"
                    }}>
                      <img 
                        src={step.imgSrc} 
                        alt={step.label} 
                        width="20" 
                        height="20" 
                        style={{ 
                          filter: status === 'inactive' ? 'grayscale(1)' : 'none',
                          objectFit: 'contain'
                        }} 
                      />
                    </div>
                    <div style={{ 
                      fontSize: "13px", 
                      fontWeight: "600", 
                      color: textColor, 
                      textAlign: "center",
                      lineHeight: "1.2",
                      maxWidth: "70px"
                    }}>
                      {step.label}
                    </div>
                    <div style={{ 
                      fontSize: "13px", 
                      color: "#6b7280", 
                      marginTop: "1px",
                      textAlign: "center"
                    }}>
                      {step.date}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Row style={{ marginBottom: "24px" }}>
            <Col md={3}>
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>Department</div>
              <div style={{ fontSize: "15px", fontWeight: "600", color: "#374151" }}>{displayDepartment}</div>
            </Col>
            <Col md={2}>
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>Ministry</div>
              <div style={{ fontSize: "15px", fontWeight: "600", color: "#374151" }}>{displayMinistry}</div>
            </Col>
            <Col md={2}>
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>Location</div>
              <div style={{ fontSize: "15px", fontWeight: "600", color: "#374151" }}>{displayLocation}</div>
            </Col>
            <Col md={3}>
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>Tender Value</div>
              <div style={{ fontSize: "15px", fontWeight: "600", color: "#374151" }}>{formatCurrency(displayValue)}</div>
            </Col>
            <Col md={2}>
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>Awarded To</div>
              <div style={{ fontSize: "15px", fontWeight: "600", color: "#2F7A87" }}>{l1Winner?.seller_name || 'Pending'}</div>
            </Col>
          </Row>
        </div>

        <div style={{ backgroundColor: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <h4 style={{ margin: 0, fontWeight: "700", fontSize: "17px", color: "#111827" }}>All Bidders</h4>
              <span style={{ fontSize: "13px", color: "#6b7280" }}>Showing {filteredBidders.length} results</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", border: "1px solid #d1d5db", borderRadius: "50px", padding: "6px 6px 6px 16px",  }}>
              <Search size={15} color="#9ca3af" />
              <input 
                type="text" 
                placeholder="Search bidders..." 
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                style={{ border: "none", outline: "none", flex: 1, marginLeft: "10px", fontSize: "14px", color: "#374151", backgroundColor: "transparent" }} 
              />
              <div style={{ backgroundColor: "#2F7A87", borderRadius: "50%", width: "30px", height: "30px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                <Search size={13} color="#ffffff" />
              </div>
            </div>
          </div>

          {safeEvaluation.length > 0 ? (
            <>
              <Table borderless hover style={{ marginBottom: 0 }}>
                <thead>
                  <tr style={{ backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                    <th style={{ color: "#6b7280", fontSize: "12px", fontWeight: "500", padding: "14px 16px", width: "6%" }}>No.</th>
                    <th style={{ color: "#6b7280", fontSize: "12px", fontWeight: "500", padding: "14px 16px", width: "8%" }}>Rank</th>
                    <th style={{ color: "#6b7280", fontSize: "12px", fontWeight: "500", padding: "14px 16px", width: "20%" }}>Bidder Name</th>
                    <th style={{ color: "#6b7280", fontSize: "12px", fontWeight: "500", padding: "14px 16px", width: "18%" }}>Offered Item</th>
                    <th style={{ color: "#6b7280", fontSize: "12px", fontWeight: "500", padding: "14px 16px", width: "14%" }}>Technical Status</th>
                    <th style={{ color: "#6b7280", fontSize: "12px", fontWeight: "500", padding: "14px 16px", width: "14%" }}>Financial Status</th>
                    <th style={{ color: "#6b7280", fontSize: "12px", fontWeight: "500", padding: "14px 16px", width: "14%" }}>Bid Amount</th>
                    <th style={{ color: "#6b7280", fontSize: "12px", fontWeight: "500", padding: "14px 16px", width: "12%" }}>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {currentBidders.map((bidder, idx) => {
                    const techStatus = getBidderStatus(bidder, 'technical');
                    const finStatus = getBidderStatus(bidder, 'financial');
                    return (
                      <tr key={bidder.id || idx} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "20px 16px", fontSize: "14px", fontWeight: "500", color: "#111827", verticalAlign: "middle" }}>
                          {idx + 1 + (currentPage - 1) * itemsPerPage}
                        </td>
                        <td style={{ padding: "20px 16px", fontSize: "14px", fontWeight: "600", color: bidder.is_winner ? "#2F7A87" : "#111827", verticalAlign: "middle" }}>
                          {bidder.rank || '-'}
                        </td>
                        <td style={{ padding: "20px 16px", fontSize: "14px", fontWeight: "500", color: bidder.is_winner ? "#2F7A87" : "#111827", verticalAlign: "middle" }}>
                          {bidder.seller_name || 'Unknown'}
                        </td>
                        <td style={{ padding: "20px 16px", fontSize: "13px", fontWeight: "400", color: "#4b5563", verticalAlign: "middle" }}>
                          {bidder.offered_item?.substring(0, 50) || 'N/A'}
                          {bidder.offered_item?.length > 50 ? '...' : ''}
                        </td>
                        <td style={{ padding: "20px 16px", fontSize: "14px", fontWeight: "500", color: getStatusColor(techStatus), verticalAlign: "middle" }}>
                          {techStatus}
                        </td>
                        <td style={{ padding: "20px 16px", fontSize: "14px", fontWeight: "500", color: getStatusColor(finStatus), verticalAlign: "middle" }}>
                          {finStatus}
                        </td>
                        <td style={{ padding: "20px 16px", fontSize: "14px", fontWeight: "600", color: "#111827", verticalAlign: "middle" }}>
                          {formatCurrency(bidder.total_price)}
                        </td>
                        <td style={{ padding: "20px 16px", fontSize: "14px", fontWeight: "500", color: "#6b7280", verticalAlign: "middle" }}>
                          {bidder.remarks || 'N/A'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "8px", paddingTop: "20px", borderTop: "1px solid #f3f4f6", marginTop: "20px" }}>
                  <button 
                    className="pagination-btn" 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                    disabled={currentPage === 1} 
                    style={{ 
                      opacity: currentPage === 1 ? 0.5 : 1,
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      backgroundColor: "#fff",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                    return (
                      <button 
                        key={pageNum} 
                        className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`} 
                        onClick={() => setCurrentPage(pageNum)}
                        style={{
                          padding: "8px 14px",
                          border: currentPage === pageNum ? "2px solid #2F7A87" : "1px solid #d1d5db",
                          borderRadius: "6px",
                          backgroundColor: currentPage === pageNum ? "#2F7A87" : "#fff",
                          color: currentPage === pageNum ? "#fff" : "#374151",
                          cursor: "pointer",
                          fontWeight: currentPage === pageNum ? "600" : "400"
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button 
                    className="pagination-btn" 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                    disabled={currentPage === totalPages} 
                    style={{ 
                      opacity: currentPage === totalPages ? 0.5 : 1,
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      backgroundColor: "#fff",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#6b7280" }}>
              <div style={{ fontSize: "16px", fontWeight: "500", marginBottom: "8px" }}>No bidders found</div>
              <div style={{ fontSize: "14px" }}>No evaluation data available for this tender</div>
            </div>
          )}
        </div>
      </Container>

      <style jsx>{`
        .pagination-btn {
          transition: all 0.2s ease;
        }
        .pagination-btn:hover:not(:disabled) {
          background-color: #f3f4f6;
        }
        .pagination-btn.active:hover {
          background-color: #1f6a7a;
        }
      `}</style>
    </div>
  );
}